import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Upload, X, ChevronDown, ArrowUp, ArrowDown, Star, Rocket, Sparkles, Send, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { assetUrl, formatApiError } from "@/lib/api";
import { FUELS, TRANSMISSIONS, BODY_TYPES, STATUSES } from "@/lib/format";
import { TID } from "@/constants/tids";

const inp =
  "bg-[#111111] border border-white/10 rounded-xl h-12 px-4 w-full text-[15px] text-white placeholder:text-white/40 outline-none transition-all duration-200 focus:bg-[#141414] focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/15";
const lbl = "block text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50 mb-2";

const EMPTY = {
  brand: "",
  model: "",
  year: new Date().getFullYear(),
  price: "",
  mileage: "",
  fuel: "Motorină",
  transmission: "Manuală",
  body_type: "Berlină",
  engine_size: "",
  power_hp: "",
  emission_standard: "Euro 6",
  drivetrain: "Față",
  consumption: "",
  color: "",
  vin: "",
  features: [],
  description: "",
  images: [],
  status: "nou",
  featured: false,
};

function Section({ title, subtitle, open, onToggle, children }) {
  return (
    <section className="bg-[#141414] rounded-3xl border border-white/10 overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 sm:p-7 text-left hover:bg-white/[0.03] transition-colors"
      >
        <div className="min-w-0">
        <h2 className="text-lg font-bold text-white">{title}</h2>
          {subtitle && (
            <p className="text-[13px] text-white/50 mt-1 truncate">{subtitle}</p>
          )}
        </div>
        <ChevronDown
          className={`h-5 w-5 text-white/40 hover:text-[#D4AF37] shrink-0 ml-3 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-5 sm:px-6 pb-6">{children}</div>}
    </section>
  );
}

export default function AdminCarForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [featureInput, setFeatureInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, failed } | null
  const [uploadSuccess, setUploadSuccess] = useState(null); // { count } | null — banner de confirmare
  const [openSections, setOpenSections] = useState({
    info: true,
    tech: false,
    price: false,
    features: false,
    images: false,
    status: false,
    publish: false,
  });

  const toggle = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/vehicles/${id}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => {
      toast.error("Mașina nu a fost găsită");
      nav("/admin/masini");
    });
  }, [id, isEdit, nav]);

  // Previne închiderea accidentală a paginii cât timp se încarcă imagini
  useEffect(() => {
    if (!uploading) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [uploading]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const addFeature = () => {
    const f = featureInput.trim();
    if (!f) return;
    setForm({ ...form, features: [...form.features, f] });
    setFeatureInput("");
  };

  const removeFeature = (i) => {
    setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) });
  };

  const uploadImages = async (files) => {
    setUploading(true);
    const total = files.length;
    let done = 0;
    let failed = 0;
    setUploadProgress({ done: 0, total, failed: 0 });

    // Snapshot al imaginilor existente înainte de acest batch — le păstrăm nemodificate
    const baseline = form.images;
    // Slot pentru fiecare fișier, indexat după ordinea originală din file picker
    const results = new Array(total).fill(null);

    // Cu limită de 4 concurenți ca să nu sufoce serverul
    const CONCURRENCY = 4;
    // Coadă cu index-uri, nu doar fișiere — păstrăm ordinea originală
    const queue = files.map((file, i) => ({ file, i }));

    const rebuildImages = () => {
      // Adaugă doar sloturile completate cu succes, în ordinea originală
      const finished = results.filter((u) => typeof u === "string");
      setForm((f) => ({ ...f, images: [...baseline, ...finished] }));
    };

    const worker = async () => {
      while (queue.length) {
        const job = queue.shift();
        if (!job) break;
        const { file, i } = job;
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/media/upload", fd);
          results[i] = data.url;
          rebuildImages(); // update progresiv, mereu în ordinea originală
        } catch (err) {
          results[i] = false; // marchează ca eșuat (nu-l mai includem)
          failed += 1;
          console.error("Upload eșuat pentru", file?.name, err);
        } finally {
          done += 1;
          setUploadProgress({ done, total, failed });
        }
      }
    };

    try {
      const workers = Array.from({ length: Math.min(CONCURRENCY, total) }, worker);
      await Promise.all(workers);
      const okCount = total - failed;
      if (okCount > 0) toast.success(`${okCount} din ${total} imagini încărcate`);
      if (failed > 0) toast.error(`${failed} imagine(i) nu s-au putut încărca`);
      // Banner verde de confirmare doar când toate au reușit
      if (failed === 0 && okCount > 0) {
        setUploadSuccess({ count: okCount });
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // Șterge banner-ul verde după 3 secunde
  useEffect(() => {
    if (!uploadSuccess) return;
    const t = setTimeout(() => setUploadSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [uploadSuccess]);

  const removeImage = (idx) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });
  };

  const moveImage = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= form.images.length) return;
    const arr = [...form.images];
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    setForm({ ...form, images: arr });
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const arr = [...form.images];
    const [pick] = arr.splice(idx, 1);
    arr.unshift(pick);
    setForm({ ...form, images: arr });
  };

  const save = async (e) => {
    e.preventDefault();
    if (uploading) {
      toast.error("Așteaptă finalizarea încărcării imaginilor înainte de a salva.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year),
        price: parseFloat(form.price),
        mileage: parseInt(form.mileage),
        engine_size: form.engine_size ? parseFloat(form.engine_size) : null,
        power_hp: form.power_hp ? parseInt(form.power_hp) : null,
        consumption: form.consumption ? parseFloat(form.consumption) : null,
      };
      if (isEdit) {
        await api.put(`/vehicles/${id}`, payload);
        toast.success("Mașina a fost actualizată");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Mașina a fost adăugată");
      }
      nav("/admin/masini");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8 min-h-screen bg-[#0A0A0A] p-4 sm:p-6 lg:p-8 pb-32">
      <div className="max-w-5xl mx-auto">
      <Link
        to="/admin/masini"
        className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-[#D4AF37] mb-4 sm:mb-6 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Înapoi la mașini
      </Link>

      <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-6 sm:mb-8">
        {isEdit ? "Editează mașină" : "Adaugă mașină"}
      </h1>

      <form onSubmit={save} className="space-y-3 sm:space-y-4">
        <Section
          title="Informații generale"
          subtitle={form.brand || form.model ? `${form.brand} ${form.model}` : "Marcă, model, an"}
          open={openSections.info}
          onToggle={() => toggle("info")}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>Marcă *</label>
              <input required className={inp} value={form.brand} onChange={set("brand")} autoComplete="off" />
            </div>
            <div>
              <label className={lbl}>Model *</label>
              <input required className={inp} value={form.model} onChange={set("model")} autoComplete="off" />
            </div>
            <div>
              <label className={lbl}>An *</label>
              <input required type="number" inputMode="numeric" className={inp} value={form.year} onChange={set("year")} />
            </div>
            <div>
              <label className={lbl}>Kilometraj *</label>
              <input required type="number" inputMode="numeric" className={inp} value={form.mileage} onChange={set("mileage")} />
            </div>
            <div>
              <label className={lbl}>Culoare</label>
              <input className={inp} value={form.color || ""} onChange={set("color")} />
            </div>
            <div>
              <label className={lbl}>VIN</label>
              <input className={inp} value={form.vin || ""} onChange={set("vin")} autoComplete="off" />
            </div>
          </div>
        </Section>

        <Section
          title="Specificații tehnice"
          subtitle={`${form.fuel} · ${form.transmission} · ${form.body_type}`}
          open={openSections.tech}
          onToggle={() => toggle("tech")}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>Combustibil</label>
              <select className={inp} value={form.fuel} onChange={set("fuel")}>
                {FUELS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Cutie</label>
              <select className={inp} value={form.transmission} onChange={set("transmission")}>
                {TRANSMISSIONS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Caroserie</label>
              <select className={inp} value={form.body_type} onChange={set("body_type")}>
                {BODY_TYPES.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Motor (L)</label>
              <input type="number" inputMode="decimal" step="0.1" className={inp} value={form.engine_size || ""} onChange={set("engine_size")} />
            </div>
            <div>
              <label className={lbl}>Putere (CP)</label>
              <input type="number" inputMode="numeric" className={inp} value={form.power_hp || ""} onChange={set("power_hp")} />
            </div>
            <div>
              <label className={lbl}>Consum (L/100km)</label>
              <input type="number" inputMode="decimal" step="0.1" className={inp} value={form.consumption || ""} onChange={set("consumption")} />
            </div>
            <div>
              <label className={lbl}>Normă poluare</label>
              <input className={inp} value={form.emission_standard || ""} onChange={set("emission_standard")} />
            </div>
            <div>
              <label className={lbl}>Tracțiune</label>
              <select className={inp} value={form.drivetrain || "Față"} onChange={set("drivetrain")}>
                <option>Față</option>
                <option>Spate</option>
                <option>4x4</option>
              </select>
            </div>
          </div>
        </Section>

        <Section
          title="Preț"
          subtitle={form.price ? `€ ${Number(form.price).toLocaleString("ro-RO")}` : "Setează prețul"}
          open={openSections.price}
          onToggle={() => toggle("price")}
        >
          <div>
            <label className={lbl}>Preț (€) *</label>
            <input required type="number" inputMode="decimal" className={inp} value={form.price} onChange={set("price")} />
            <p className="mt-2 text-xs text-white/50">TVA inclus. Se afișează în lista publică și pe pagina mașinii.</p>
          </div>
        </Section>

        <Section
          title="Dotări și descriere"
          subtitle={`${form.features.length} dotări · ${form.description.length} caractere`}
          open={openSections.features}
          onToggle={() => toggle("features")}
        >
          <div>
            <label className={lbl}>Adaugă dotare</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                className={inp}
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addFeature();
                  }
                }}
                placeholder="ex. Navigație, Piele, Cameră marșarier"
              />
              <button
                type="button"
                onClick={addFeature}
                className="px-5 h-12 rounded-xl bg-[#D4AF37] hover:bg-[#E4BF47] text-[#0A0A0A] text-sm font-bold shrink-0 transition-colors"
              >
                Adaugă
              </button>
            </div>
            {form.features.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {form.features.map((f, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full pl-3 pr-1 py-1 text-xs font-semibold text-white"
                  >
                    {f}
                    <button
                      type="button"
                      onClick={() => removeFeature(i)}
                      className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/15"
                    >
                      <X className="h-3 w-3 text-white/60" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="mt-6">
            <label className={lbl}>Descriere</label>
            <textarea
              className={`${inp} !h-auto min-h-[14rem] py-3 resize-y leading-relaxed`}
              value={form.description}
              onChange={set("description")}
              placeholder="Descrie starea mașinii, istoricul, dotările speciale..."
            />
          </div>
        </Section>

        <Section
          title="Galerie foto"
          subtitle={`${form.images.length} imagini`}
          open={openSections.images}
          onToggle={() => toggle("images")}
        >
          {uploadSuccess && (
            <div
              role="status"
              data-testid="upload-success-banner"
              className="mb-4 flex items-start gap-3 rounded-2xl border border-[#22C55E]/40 bg-[#22C55E]/10 p-4 animate-in fade-in slide-in-from-top-2 duration-300"
            >
              <span className="h-9 w-9 rounded-xl bg-[#22C55E]/20 border border-[#22C55E]/40 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-5 w-5 text-[#4ADE80]" strokeWidth={2.4} />
              </span>
              <div className="min-w-0">
                <div className="text-[14px] font-bold text-[#86EFAC] leading-snug">
                  {uploadSuccess.count === 1
                    ? "Imaginea a fost încărcată cu succes."
                    : `Toate cele ${uploadSuccess.count} de imagini au fost încărcate cu succes.`}
                </div>
                <div className="text-[12px] text-[#86EFAC]/80 mt-0.5">
                  Acum puteți salva mașina.
                </div>
              </div>
            </div>
          )}
          <label
            htmlFor="carupload"
            className={`flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-white/15 hover:border-[#D4AF37] active:border-[#D4AF37] cursor-pointer transition-colors ${
              uploading ? "opacity-70 cursor-progress" : ""
            }`}
          >
            <Upload className="h-6 w-6 text-white/50" />
            <span className="text-sm font-semibold text-white/70 text-center px-4">
              {uploading
                ? uploadProgress
                  ? `Se încarcă ${uploadProgress.done}/${uploadProgress.total}...`
                  : "Se încarcă..."
                : "Apasă pentru a alege imagini"}
            </span>
            <span className="text-[10px] text-white/40">JPG · PNG · WEBP</span>
            {uploading && uploadProgress && (
              <div className="w-full max-w-xs px-6 -mt-1">
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-[#D4AF37] transition-all duration-300"
                    style={{
                      width: `${Math.round((uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100)}%`,
                    }}
                  />
                </div>
                {uploadProgress.failed > 0 && (
                  <div className="mt-1 text-[10px] font-semibold text-red-400 text-center">
                    {uploadProgress.failed} eșuate
                  </div>
                )}
              </div>
            )}
            <input
              id="carupload"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                if (e.target.files?.length) uploadImages([...e.target.files]);
                e.target.value = "";
              }}
            />
          </label>
          {form.images.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {form.images.map((src, i) => (
                <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black/40 border border-white/10">
                  <img src={assetUrl(src)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[#D4AF37] text-white">
                      Principală
                    </span>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex items-center justify-between">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveImage(i, -1)}
                        disabled={i === 0}
                        className="h-8 w-8 rounded-full bg-white/90 text-[#0A0A0A] flex items-center justify-center disabled:opacity-30"
                        title="Mută înainte"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveImage(i, 1)}
                        disabled={i === form.images.length - 1}
                        className="h-8 w-8 rounded-full bg-white/90 text-[#0A0A0A] flex items-center justify-center disabled:opacity-30"
                        title="Mută înapoi"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-1">
                      {i !== 0 && (
                        <button
                          type="button"
                          onClick={() => setPrimary(i)}
                          className="h-8 w-8 rounded-full bg-white/90 text-[#B5952F] flex items-center justify-center"
                          title="Setează principală"
                        >
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="h-8 w-8 rounded-full bg-red-600 text-white flex items-center justify-center"
                        title="Șterge"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section
          title="Status & vizibilitate"
          subtitle={`${STATUSES.find((s) => s.value === form.status)?.label}${form.featured ? " · Recomandată" : ""}`}
          open={openSections.status}
          onToggle={() => toggle("status")}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Status</label>
              <select className={inp} value={form.status} onChange={set("status")}>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-3 cursor-pointer bg-white/5 border border-white/10 rounded-xl px-4 h-12 hover:bg-white/[0.07] transition-colors">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={set("featured")}
                className="h-5 w-5 accent-[#D4AF37]"
              />
              <span className="text-sm font-semibold text-white">
                Afișează în „Mașini recomandate”
              </span>
            </label>
          </div>
        </Section>

        {/* ---------- Publicare pe platforme (Coming Soon preview) ---------- */}
        <PublishingSection
          open={openSections.publish}
          onToggle={() => toggle("publish")}
          vehicleId={isEdit ? id : null}
          images={form.images}
        />
      </form>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10 p-4 z-30 shadow-[0_-8px_30px_rgba(0,0,0,0.5)]">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Link
            to="/admin/masini"
            className="flex-1 sm:flex-initial h-12 px-6 rounded-full border border-white/15 text-sm font-semibold text-white hover:bg-white/5 hover:border-white/25 transition-colors inline-flex items-center justify-center"
          >
            Anulează
          </Link>
          <button
            data-testid={TID.adminCarSave}
            onClick={save}
            disabled={saving || uploading}
            title={uploading ? "Așteaptă finalizarea încărcării imaginilor" : ""}
            className="flex-1 h-12 px-6 rounded-full bg-[#D4AF37] hover:bg-[#E4BF47] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)] active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none text-[#0A0A0A] text-sm font-bold"
          >
            {uploading
              ? uploadProgress
                ? `Se încarcă ${uploadProgress.done}/${uploadProgress.total}...`
                : "Se încarcă imaginile..."
              : saving
              ? "Se salvează..."
              : isEdit
              ? "Actualizează"
              : "Adaugă mașina"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================== Publicare pe platforme — preview ==================== */
const PLATFORMS = [
  { name: "Facebook Marketplace", short: "Marketplace", bg: "#1877F2", txt: "#fff", type: "fb" },
  { name: "Instagram", short: "Instagram", bg: "linear-gradient(135deg, #f9ce34 0%, #ee2a7b 45%, #6228d7 100%)", txt: "#fff", type: "ig" },
  { name: "TikTok", short: "TikTok", bg: "#000", txt: "#fff", type: "tt" },
  { name: "OLX", short: "OLX", bg: "#002F34", txt: "#7ed957", type: "olx" },
  { name: "Autovit", short: "Autovit", bg: "#ee1f26", txt: "#fff", type: "text" },
  { name: "Publi24", short: "Publi24", bg: "#f26522", txt: "#fff", type: "text" },
  { name: "Lajumate", short: "Lajumate", bg: "#0B2E4F", txt: "#f6c343", type: "text" },
  { name: "CarZZZ", short: "CarZZZ", bg: "#1a68e6", txt: "#fff", type: "text" },
];

function PlatformLogo({ p }) {
  const style = {
    background: p.bg,
    color: p.txt,
  };
  if (p.type === "fb") {
    return (
      <div className="h-11 w-11 rounded-xl flex items-center justify-center font-black text-[22px]" style={style}>
        f
      </div>
    );
  }
  if (p.type === "ig") {
    return (
      <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={style}>
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
        </svg>
      </div>
    );
  }
  if (p.type === "tt") {
    return (
      <div className="h-11 w-11 rounded-xl flex items-center justify-center relative" style={style}>
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
          <path d="M16.5 3c.36 2.2 1.6 3.67 3.7 3.9v2.53c-1.22.12-2.29-.28-3.53-1.03v6.36c0 4.1-3.6 6.6-7.2 5.16-2.32-.93-3.47-3.35-2.9-5.8.55-2.37 2.86-3.98 5.32-3.6v2.6c-.38-.06-.79-.09-1.18-.02-1.13.2-1.87 1.2-1.72 2.32.15 1.13 1.2 1.9 2.4 1.66 1.05-.2 1.78-1.13 1.78-2.24V3h1.53z" />
        </svg>
      </div>
    );
  }
  if (p.type === "olx") {
    return (
      <div className="h-11 w-11 rounded-xl flex items-center justify-center font-black text-[15px] tracking-tight" style={style}>
        OLX
      </div>
    );
  }
  return (
    <div className="h-11 w-11 rounded-xl flex items-center justify-center font-extrabold text-[11px] tracking-tight text-center leading-none px-1" style={style}>
      {p.short}
    </div>
  );
}

function PlatformCard({ p, delay, onPublish, publishing }) {
  const isFb = p.type === "fb";
  const isEnabled = isFb && typeof onPublish === "function";
  const isBusy = isFb && publishing;

  return (
    <div
      className="group relative rounded-2xl border border-white/10 hover:border-[#D4AF37]/60 bg-white/[0.03] hover:bg-white/[0.06] p-4 sm:p-5 flex items-center gap-4 hover:shadow-[0_15px_35px_-15px_rgba(212,175,55,0.35)] hover:-translate-y-0.5 transition-all duration-300"
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/0 to-transparent group-hover:via-[#D4AF37]/40 transition-colors" />
      <PlatformLogo p={p} />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-bold text-white truncate">
          {p.name}
        </div>
        {isEnabled ? (
          <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#1877F2]/15 border border-[#1877F2]/40 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-[#4DA3FF]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#4DA3FF] animate-pulse" />
            Activ
          </div>
        ) : (
          <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/5 border border-[#D4AF37]/30 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            În dezvoltare
          </div>
        )}
      </div>
      <button
        type="button"
        disabled={!isEnabled || isBusy}
        aria-disabled={!isEnabled || isBusy}
        onClick={isEnabled ? onPublish : undefined}
        title={isEnabled ? "Publică pe Facebook" : "Funcționalitate în dezvoltare"}
        data-testid={isFb ? "publish-facebook-btn" : undefined}
        className={
          isEnabled
            ? "shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#1877F2] hover:bg-[#166FE5] border border-[#1877F2] text-white text-[12px] font-bold uppercase tracking-[0.14em] cursor-pointer shadow-[0_6px_16px_-6px_rgba(24,119,242,0.55)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-wait"
            : "shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white/5 border border-white/10 text-white/40 text-[12px] font-bold uppercase tracking-[0.14em] cursor-not-allowed"
        }
      >
        <Send className="h-3.5 w-3.5" />
        {isBusy ? "Se publică..." : "Publică"}
      </button>
    </div>
  );
}

function PublishingSection({ open, onToggle, vehicleId, images }) {
  const [publishingFb, setPublishingFb] = useState(false);

  const publishFacebook = async () => {
    if (publishingFb) return;
    if (!vehicleId) {
      toast.error("Salvează mai întâi mașina înainte de a o publica pe Facebook.");
      return;
    }
    if (!images || images.length === 0) {
      toast.error("Adaugă cel puțin o fotografie înainte de publicare.");
      return;
    }
    setPublishingFb(true);
    try {
      await api.post(`/vehicles/${vehicleId}/publish/facebook`);
      toast.success("Anunț publicat cu succes pe Facebook.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPublishingFb(false);
    }
  };

  return (
    <section className="bg-[#141414] rounded-3xl border border-white/10 overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-white/[0.03] active:bg-white/[0.05] transition-colors"
      >
        <div className="min-w-0 flex-1 flex items-start sm:items-center gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-white">
              Publicare pe platforme
            </h2>
            <p className="text-xs text-white/50 mt-0.5">
              Publică rapid anunțul pe mai multe platforme dintr-un singur loc.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-[#D4AF37]/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            În lucru
          </span>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-white/40 shrink-0 ml-3 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-6 space-y-6">
          {/* Platforms grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {PLATFORMS.map((p, i) => (
              <PlatformCard
                key={p.name}
                p={p}
                delay={i * 40}
                onPublish={p.type === "fb" ? publishFacebook : undefined}
                publishing={p.type === "fb" ? publishingFb : false}
              />
            ))}
          </div>

          {/* Info banner */}
          <div className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/25 bg-gradient-to-br from-[#1A1611] via-[#141210] to-[#0F0E0C] p-5 sm:p-7">
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#D4AF37]/20 blur-3xl" />
            <div className="relative flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.4)]">
                <Rocket className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[15px] sm:text-base font-extrabold text-white">
                    Publicare automată — În dezvoltare
                  </h3>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]">
                    Coming soon
                  </span>
                </div>
                <p className="mt-2 text-[13px] sm:text-[14px] text-white/70 leading-relaxed max-w-3xl">
                  Într-o versiune viitoare vei putea publica simultan același
                  anunț pe Facebook Marketplace, Instagram, TikTok, OLX,
                  Autovit, Publi24, Lajumate și alte platforme, direct din
                  AutoRIO, fără completarea manuală a fiecărui anunț.
                </p>
              </div>
            </div>
          </div>

          {/* Flow diagram */}
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-8">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] mb-5 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Cum va funcționa
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
              {/* Vertical flow steps */}
              <ol className="relative space-y-4 md:min-w-[260px]">
                <div
                  aria-hidden="true"
                  className="absolute left-4 top-4 bottom-4 w-px bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/40 to-[#D4AF37]/10"
                />
                {[
                  { title: "AutoRIO CMS", desc: "Adaugi mașina o singură dată" },
                  { title: "Pregătire anunț", desc: "Titlu, descriere și foto adaptate" },
                  { title: "Publicare automată", desc: "Simultan pe toate platformele alese" },
                ].map((s, i) => (
                  <li key={s.title} className="relative pl-12">
                    <span className="absolute left-0 top-1 h-8 w-8 rounded-full bg-[#0A0A0A] border border-[#D4AF37]/50 text-[#D4AF37] font-display font-extrabold text-[13px] flex items-center justify-center ring-4 ring-[#141414]">
                      {i + 1}
                    </span>
                    <div className="text-[14px] font-bold text-white leading-tight">
                      {s.title}
                    </div>
                    <div className="text-[12px] text-white/50 mt-0.5">
                      {s.desc}
                    </div>
                  </li>
                ))}
              </ol>

              {/* Right — destinations pills */}
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 mb-3">
                  Distribuție simultană către
                </div>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => (
                    <span
                      key={p.name}
                      className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 pl-1 pr-3 py-1"
                    >
                      <span
                        className="h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-black shrink-0"
                        style={{
                          background: p.bg,
                          color: p.txt,
                        }}
                      >
                        {p.short.slice(0, 2)}
                      </span>
                      <span className="text-[12px] font-semibold text-white">
                        {p.short}
                      </span>
                    </span>
                  ))}
                </div>

                {/* Availability badge */}
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-4 py-2">
                  <span className="h-2 w-2 rounded-full bg-[#D4AF37] animate-pulse" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]">
                    Disponibil într-un update viitor
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
